import React, { useState } from "react";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { EmailCanvasV2 } from "./EmailCanvasV2";

import { EmailPreview } from "./EmailPreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, Send, Save, Plus, Type, Image, MousePointer, Layout, Minus, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export interface EmailComponent {
  id: string;
  type: "header" | "text" | "image" | "button" | "footer" | "divider";
  content: any;
  style: any;
}

interface EmailBuilderV2Props {
  initialTemplate?: {
    name: string;
    subject: string;
    components: EmailComponent[];
  };
  onSave?: (template: any) => void;
  onSend?: (email: any) => void;
}

const COMPONENT_TYPES = [
  { type: "header", icon: Layout, label: "Header" },
  { type: "text", icon: Type, label: "Text" },
  { type: "image", icon: Image, label: "Image" },
  { type: "button", icon: MousePointer, label: "Button" },
  { type: "divider", icon: Minus, label: "Divider" },
  { type: "footer", icon: Mail, label: "Footer" },
];

export const EmailBuilderV2 = ({ initialTemplate, onSave, onSend }: EmailBuilderV2Props) => {
  const [templateName, setTemplateName] = useState(initialTemplate?.name || "");
  const [subject, setSubject] = useState(initialTemplate?.subject || "");
  const [components, setComponents] = useState<EmailComponent[]>(initialTemplate?.components || []);
  const [showPreview, setShowPreview] = useState(false);
  const [editingComponent, setEditingComponent] = useState<string | null>(null);
  const [showComponentToolbar, setShowComponentToolbar] = useState(false);
  const { toast } = useToast();

  const addComponent = (type: string) => {
    const newComponent: EmailComponent = {
      id: `${type}-${Date.now()}`,
      type: type as any,
      content: getDefaultContent(type),
      style: getDefaultStyle(type),
    };
    setComponents(prev => [...prev, newComponent]);
    setEditingComponent(newComponent.id);
    setShowComponentToolbar(false);
  };

  const getDefaultContent = (type: string) => {
    switch (type) {
      case "header": return { text: "Your Header Text", logoUrl: "" };
      case "text": return { text: "Your text content goes here..." };
      case "image": return { url: "", alt: "Image description", caption: "" };
      case "button": return { text: "Click Me", url: "#" };
      case "footer": return { text: "© 2024 Your Company. All rights reserved." };
      case "divider": return {};
      default: return {};
    }
  };

  const getDefaultStyle = (type: string) => {
    switch (type) {
      case "header":
        return { 
          backgroundColor: "hsl(var(--primary))", 
          color: "hsl(var(--primary-foreground))",
          padding: "24px",
          textAlign: "center"
        };
      case "text":
        return { 
          fontSize: "16px", 
          color: "hsl(var(--foreground))",
          padding: "16px",
          textAlign: "left"
        };
      case "image":
        return { 
          width: "100%", 
          padding: "16px",
          textAlign: "center"
        };
      case "button":
        return { 
          backgroundColor: "hsl(var(--primary))", 
          color: "hsl(var(--primary-foreground))",
          padding: "12px 24px",
          borderRadius: "6px",
          textAlign: "center",
          margin: "16px"
        };
      case "footer":
        return { 
          backgroundColor: "hsl(var(--muted))", 
          color: "hsl(var(--muted-foreground))",
          padding: "24px",
          textAlign: "center",
          fontSize: "14px"
        };
      case "divider":
        return { 
          height: "1px", 
          backgroundColor: "hsl(var(--border))",
          margin: "16px 0"
        };
      default: return {};
    }
  };

  const updateComponent = (componentId: string, updates: Partial<EmailComponent>) => {
    setComponents(prev => 
      prev.map(comp => 
        comp.id === componentId ? { ...comp, ...updates } : comp
      )
    );
  };

  const deleteComponent = (componentId: string) => {
    setComponents(prev => prev.filter(comp => comp.id !== componentId));
    if (editingComponent === componentId) {
      setEditingComponent(null);
    }
  };

  const moveComponent = (componentId: string, direction: 'up' | 'down') => {
    const index = components.findIndex(c => c.id === componentId);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= components.length) return;
    
    const newComponents = [...components];
    [newComponents[index], newComponents[newIndex]] = [newComponents[newIndex], newComponents[index]];
    setComponents(newComponents);
  };

  const duplicateComponent = (componentId: string) => {
    const component = components.find(c => c.id === componentId);
    if (!component) return;
    
    const duplicate = {
      ...component,
      id: `${component.type}-${Date.now()}`
    };
    
    const index = components.findIndex(c => c.id === componentId);
    const newComponents = [...components];
    newComponents.splice(index + 1, 0, duplicate);
    setComponents(newComponents);
  };

  const handleSave = () => {
    const template = {
      name: templateName,
      subject,
      components,
      html_content: generateHTML(),
      json_content: { components }
    };
    
    onSave?.(template);
    toast({
      title: "Template saved",
      description: "Your email template has been saved successfully.",
    });
  };

  const handleSend = () => {
    const email = {
      subject,
      html_content: generateHTML(),
      components
    };
    
    onSend?.(email);
    toast({
      title: "Email sent",
      description: "Your email has been sent successfully.",
    });
  };

  const generateHTML = () => {
    let html = `
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
            .email-container { max-width: 600px; margin: 0 auto; }
          </style>
        </head>
        <body>
          <div class="email-container">
    `;

    components.forEach(component => {
      switch (component.type) {
        case "header":
          html += `<div style="background-color: ${component.style.backgroundColor}; color: ${component.style.color}; padding: ${component.style.padding}; text-align: ${component.style.textAlign};">
            <h1>${component.content.text}</h1>
          </div>`;
          break;
        case "text":
          html += `<div style="padding: ${component.style.padding}; font-size: ${component.style.fontSize}; color: ${component.style.color}; text-align: ${component.style.textAlign};">
            ${component.content.text}
          </div>`;
          break;
        case "image":
          const imageStyles = `
            max-width: 100%; 
            height: auto; 
            width: ${component.style.imageWidth || 'auto'}; 
            height: ${component.style.imageHeight || 'auto'};
            ${component.style.imageFloat && component.style.imageFloat !== 'none' ? `float: ${component.style.imageFloat}; margin: ${component.style.imageFloat === 'left' ? '0 16px 16px 0' : '0 0 16px 16px'};` : ''}
          `;
          html += `<div style="padding: ${component.style.padding}; text-align: ${component.style.textAlign};">
            <img src="${component.content.url}" alt="${component.content.alt || 'Image'}" style="${imageStyles}" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';" />
            <div style="display:none; padding: 20px; background: #f5f5f5; text-align: center; color: #666; border: 2px dashed #ccc;">
              Image preview not available (${component.content.alt || 'uploaded image'})
            </div>
            ${component.content.caption ? `<p style="margin-top: 8px; font-size: 14px; color: #666;">${component.content.caption}</p>` : ''}
          </div>`;
          break;
        case "button":
          html += `<div style="text-align: ${component.style.textAlign}; margin: ${component.style.margin};">
            <a href="${component.content.url}" style="background-color: ${component.style.backgroundColor}; color: ${component.style.color}; padding: ${component.style.padding}; border-radius: ${component.style.borderRadius}; text-decoration: none; display: inline-block;">
              ${component.content.text}
            </a>
          </div>`;
          break;
        case "footer":
          html += `<div style="background-color: ${component.style.backgroundColor}; color: ${component.style.color}; padding: ${component.style.padding}; text-align: ${component.style.textAlign}; font-size: ${component.style.fontSize};">
            ${component.content.text}
          </div>`;
          break;
        case "divider":
          html += `<hr style="height: ${component.style.height}; background-color: ${component.style.backgroundColor}; border: none; margin: ${component.style.margin};">`;
          break;
      }
    });

    html += `
          </div>
        </body>
      </html>
    `;

    return html;
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="border-b bg-background p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Enter template name"
                className="w-64"
              />
            </div>
            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
                className="w-80"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowPreview(true)}>
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button variant="outline" onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            <Button onClick={handleSend}>
              <Send className="h-4 w-4 mr-2" />
              Send
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col bg-muted/20 p-6 overflow-auto">
        <div className="max-w-4xl mx-auto w-full">
          {/* Add Component Bar */}
          <div className="mb-4 flex items-center justify-center">
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setShowComponentToolbar(!showComponentToolbar)}
                className="bg-white shadow-sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Component
              </Button>
              
              {showComponentToolbar && (
                <div className="absolute top-12 left-1/2 transform -translate-x-1/2 bg-white border rounded-lg shadow-lg p-2 z-10">
                  <div className="flex gap-1">
                    {COMPONENT_TYPES.map(({ type, icon: Icon, label }) => (
                      <Button
                        key={type}
                        variant="ghost"
                        size="sm"
                        onClick={() => addComponent(type)}
                        className="flex flex-col h-16 w-16 p-1"
                        title={label}
                      >
                        <Icon className="h-5 w-5 mb-1" />
                        <span className="text-xs">{label}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Email Canvas */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Email Preview</CardTitle>
              {components.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Click "Add Component" above to start building your email
                </p>
              )}
            </CardHeader>
            <CardContent>
              <EmailCanvasV2
                components={components}
                editingComponent={editingComponent}
                onEditComponent={setEditingComponent}
                onUpdateComponent={updateComponent}
                onDeleteComponent={deleteComponent}
                onMoveComponent={moveComponent}
                onDuplicateComponent={duplicateComponent}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <EmailPreview
          subject={subject}
          htmlContent={generateHTML()}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};